import { ManagementAccountFieldName, ManagementAccountFieldPermissions, Roles } from '@common/types/user';
import { PHONE_REGEX_STRICT } from '@constants/schema';
import { isFieldRequiredByPermission } from '@utils/fieldPermissions';
import * as Yup from 'yup';

const mobilePhoneSchema = Yup.string().nullable().matches(PHONE_REGEX_STRICT, 'Mobile phone number not valid');
const phoneSchema = Yup.string().nullable().matches(PHONE_REGEX_STRICT, 'Phone number not valid');
const faxPhoneSchema = Yup.string().nullable().matches(PHONE_REGEX_STRICT, 'Fax phone number not valid');

type NullableStringSchema = Yup.StringSchema<string | null | undefined>;
type NullableNumberSchema = Yup.NumberSchema<number | null | undefined>;

const withFieldPermissions = <TSchema extends Yup.AnySchema>(
    fieldName: ManagementAccountFieldName,
    schema: TSchema,
    readOnlySchema: TSchema
): TSchema => {
    const dependencies = ['field_permissions'];
    if (fieldName !== 'role') {
        dependencies.push('role');
    }
    if (fieldName === 'job_title_other') {
        dependencies.push('job_title');
    }

    return schema.when(dependencies, (values, currentSchema) => {
        const permissions = values[0] as ManagementAccountFieldPermissions | null | undefined;
        const permission = permissions?.[fieldName];
        if (permission?.read_only) {
            return readOnlySchema;
        }

        const role = fieldName === 'role' ? undefined : (values[1] as Roles | undefined);
        const jobTitle = fieldName === 'job_title_other' ? (values[2] as string | undefined) : undefined;

        let resolvedSchema = currentSchema;
        if (fieldName === 'role' && permission?.allowed_values) {
            resolvedSchema = resolvedSchema.oneOf(permission.allowed_values, 'This role is not allowed.');
        }
        if (isFieldRequiredByPermission({ permission, role, jobTitle })) {
            resolvedSchema = resolvedSchema.required('This field is required.');
        }

        return resolvedSchema;
    }) as TSchema;
};

const stringField = (fieldName: ManagementAccountFieldName, schema: NullableStringSchema): NullableStringSchema =>
    withFieldPermissions(fieldName, schema, Yup.string().nullable()) as NullableStringSchema;

const numberField = (fieldName: ManagementAccountFieldName, schema: NullableNumberSchema): NullableNumberSchema =>
    withFieldPermissions(fieldName, schema, Yup.number().nullable()) as NullableNumberSchema;

export const memberSchema = Yup.object().shape({
    role: stringField('role', Yup.string().nullable()),
    job_title: stringField('job_title', Yup.string().nullable()),
    job_title_other: stringField('job_title_other', Yup.string().nullable()),
    field_permissions: Yup.mixed<ManagementAccountFieldPermissions>().nullable(),
    first_name: stringField('first_name', Yup.string().nullable()),
    last_name: stringField('last_name', Yup.string().nullable()),
    mobile_phone: stringField('mobile_phone', mobilePhoneSchema),
    telephone: stringField('telephone', phoneSchema),
    fax: stringField('fax', faxPhoneSchema),
    email: stringField('email', Yup.string().email('Invalid email').nullable()),
    sales_manager: numberField('sales_manager', Yup.number().nullable()),
    company_name: stringField('company_name', Yup.string().nullable()),
    distributor: numberField('distributor', Yup.number().nullable()),
    country: stringField('country', Yup.string().nullable()),
    state: stringField('state', Yup.string().nullable()),
    city: stringField('city', Yup.string().nullable()),
    address1: stringField('address1', Yup.string().nullable()),
    address2: stringField('address2', Yup.string().nullable()),
    language: stringField('language', Yup.string().nullable()),
    food_preference: stringField('food_preference', Yup.string().nullable()),
});
